/*
  Level Pegs Tee Club selector.
  Progressive enhancement over snippets/tee-club-selector.liquid.

  Responsibilities:
  - map the chosen quantity + add-on to the correct existing variant id
  - keep the hidden variant id in sync with the form (the selling_plan radios are
    form-associated and post on their own)
  - show the price for the chosen variant and selling plan, taken from the JSON
    the snippet renders from Shopify's selling_plan_allocations (no maths here)
  - keep the summary, tile prices and add-on price current
  - optionally mirror the selection into the URL (?variant=&selling_plan=)

  Adding to the cart is handled by Dawn's product-form element (product-form.js).
*/
if (!customElements.get('tee-club-selector')) {
  customElements.define(
    'tee-club-selector',
    class TeeClubSelector extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-tc-data]');
        if (!dataEl) return;

        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('Tee Club selector: could not read price data', e);
          return;
        }

        this.variants = new Map(this.data.variants.map((v) => [String(v.id), v]));
        this.qtyInputs = Array.from(this.querySelectorAll('input[data-tc-qty]'));
        this.planInputs = Array.from(this.querySelectorAll('input[data-tc-plan]'));
        this.addonInput = this.querySelector('input[data-tc-addon]');
        this.idInput = this.querySelector('input[name="id"]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitLabel = this.querySelector('[data-tc-submit-label]');
        this.addonPrice = this.querySelector('[data-tc-addon-price]');
        this.summary = {};
        this.querySelectorAll('[data-tc-sum]').forEach((el) => {
          this.summary[el.dataset.tcSum] = el;
        });

        if (!this.qtyInputs.length || !this.planInputs.length || !this.idInput) return;

        // Make sure something is selected even if the markup had no default.
        if (!this.checkedQty()) {
          const first = this.qtyInputs.find((i) => !i.disabled) || this.qtyInputs[0];
          first.checked = true;
        }
        if (!this.checkedPlan()) this.planInputs[0].checked = true;

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-tc-qty], [data-tc-plan], [data-tc-addon]')) this.update();
        });

        this.update();
      }

      checkedQty() {
        return this.qtyInputs.find((i) => i.checked);
      }

      checkedPlan() {
        return this.planInputs.find((i) => i.checked);
      }

      allocationFor(variant, planId) {
        if (!variant) return null;
        return variant.allocations.find((a) => String(a.plan) === String(planId)) || null;
      }

      update() {
        const qty = this.checkedQty();
        const plan = this.checkedPlan();
        if (!qty || !plan) return;

        const addon = Boolean(this.addonInput && this.addonInput.checked && qty.dataset.addonVariant);
        const variantId = addon ? qty.dataset.addonVariant : qty.value;
        const variant = this.variants.get(String(variantId));
        const baseVariant = this.variants.get(String(qty.value));
        const allocation = this.allocationFor(variant, plan.value);
        const available = Boolean(variant && variant.available && allocation);

        // 1. The value that is posted.
        this.idInput.value = variantId;

        // 2. Quantity tiles show the price at the chosen frequency.
        this.qtyInputs.forEach((input) => {
          const v = this.variants.get(String(input.value));
          const a = this.allocationFor(v, plan.value);
          const priceEl = input.closest('.tc-tile') && input.closest('.tc-tile').querySelector('[data-tc-tile-price]');
          if (!priceEl) return;
          priceEl.textContent = a ? `${a.price_short} per delivery` : v ? `${v.price_short} per delivery` : '';
        });

        // 3. Add-on price at the chosen frequency.
        if (this.addonPrice) {
          const delta = baseVariant && baseVariant.addon_delta ? baseVariant.addon_delta[String(plan.value)] : null;
          this.addonPrice.textContent = delta ? `(+ ${delta} per delivery)` : '';
        }

        // 4. Summary.
        if (this.summary.qty) this.summary.qty.textContent = `${qty.dataset.count} premium bamboo tees`;
        if (this.summary.plan) this.summary.plan.textContent = plan.dataset.label;
        if (this.summary.addon) {
          this.summary.addon.textContent = addon ? 'Ready Pegs included' : 'Ready Pegs not included';
        }
        if (this.summary.price) {
          this.summary.price.textContent = allocation
            ? allocation.price_formatted
            : variant
            ? variant.price_formatted
            : '';
        }

        // 5. Button state.
        if (this.submitButton) {
          this.submitButton.disabled = !available;
          if (available) this.submitButton.removeAttribute('aria-disabled');
          if (this.submitLabel) this.submitLabel.textContent = available ? 'Add to cart' : 'Unavailable';
        }

        // 6. URL (product page only).
        if (this.dataset.updateUrl === 'true' && window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          url.searchParams.set('selling_plan', plan.value);
          window.history.replaceState({}, '', url.toString());
        }

        // Let other theme code know (Dawn's pub/sub, when present).
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.dataset.section, variant: variant, sellingPlan: plan.value },
          });
        }
      }
    }
  );
}